import { api } from '@/api';
import { ArrayField, PrimitiveArrayField } from '@/components/array-field';
import { Page } from '@/components/dashboard/page';
import { QR } from '@/components/qr-code';
import { router } from '@/main';
import { RoomSchema, RoomsUpdateSchema, type Room, type RoomsUpdate } from '@shared/modelTypes/room-types';
import { createFileRoute } from '@tanstack/react-router';
import { Button, Heading } from 'ht-sdk/react';
import { Form, NumberField, PathProvider, TextField, joinPath, resolver, usePathContext } from 'ht-sdk/react/forms';
import { useForm, useFormContext } from 'react-hook-form';
import { toast } from 'sonner';

export const Route = createFileRoute('/dashboard/rooms/edit')({
	component: RouteComponent,
	loader: async () => {
		const res = await api.roomList({});
		return res;
	},
});

function RouteComponent() {
	const data = Route.useLoaderData();
	if (!data.success) {
		return <div>Error loading rooms</div>;
	}

	return (
		<Page>
			<Heading>Edit Rooms</Heading>
			<RoomSEditForm rooms={data.data} />
		</Page>
	);
}

function RoomSEditForm({ rooms }: { rooms: Room[] }) {
	const methods = useForm({
		resolver: resolver(RoomsUpdateSchema),
		defaultValues: { rooms },
	});

	async function onSubmit(data: RoomsUpdate) {
		console.log('onSubmit', data);

		const res = await api.roomsUpdate(data);
		if (!res.success) {
			toast.error(res.message);
			return;
		} else {
			toast.success('Rooms updated successfully');
			console.log('navigating to /dashboard/rooms');
			router.invalidate();
			router.navigate({ to: '/dashboard/rooms' });
		}
	}

	return (
		<Form methods={methods} onSubmit={onSubmit}>
			<ArrayField<Room>
				collapsible
				label="Rooms"
				name="rooms"
				addLabel="Add room"
				add={() => RoomSchema.parse({ id: crypto.randomUUID(), name: 'new room' })}
				build={(_it, _i) => {
					return <RoomForm path="" />;
				}}
				buildPreview={(_it, _i) => <Heading mode="secondary">{_it.name}</Heading>}
			/>
			<Button type="submit" className="my-3 cursor-pointer">
				Save
			</Button>
		</Form>
	);
}

function RoomForm({ path }: { path: string }) {
	const { watch } = useFormContext();
	const formPath = usePathContext(path);

	const activationCodes = watch(joinPath(formPath, 'activationCodes')) as string[];

	return (
		<PathProvider path={path}>
			<TextField name="name" label="Name" />
			<NumberField name="repairTime" label="Repair Time" />
			<PrimitiveArrayField<string>
				addLabel="Add activation code"
				add={() => ''}
				name="activationCodes"
				label="Activation Codes"
				sortable={false}
				build={(_it, _i) => {
					return (
						<div className="flex flex-row gap-2">
							<TextField name={`${_i}`} label={null} className="font-mono uppercase" />
							<div className="max-h-4">
								<QR data={activationCodes[_i]} />
							</div>
						</div>
					);
				}}
				buildPreview={(_it, _i) => <Heading mode="secondary">{_it}</Heading>}
			/>
			<PrimitiveArrayField<string>
				addLabel="Add repair code"
				add={() => ''}
				name="repairCodes"
				label="Repair Codes"
				sortable={false}
				build={(_it, _i) => <TextField name={`${_i}`} label={null} className="font-mono uppercase" />}
				buildPreview={(_it, _i) => <Heading mode="secondary">{_it}</Heading>}
			/>
		</PathProvider>
	);
}
